import type { ReactNode } from 'react'
import { List, Stack, Text } from '@mantine/core'
import { MetaList } from '@lighthouse/design'
import { NOT_MEASURED, exactNumber, whenLabel } from '../../utils/format'
import { durationLabel } from './scheduleFormat'
import type { ScheduleLoadCost } from '../../types/scheduleLoad'

/**
 * Everything one series' cost rests on, so the estimate can be disbelieved (S3).
 *
 * The server serves the percentiles AND their inputs — how many samples, from
 * when to when, what was discarded and why, whether the log is shared, whether
 * the last run failed — and this renders all of them. An estimate shown without
 * its sample count is a number an administrator cannot argue with, which is the
 * opposite of what this page is for: the admin overrides the model.
 *
 * Rows that only apply in some cases (a shared log, a stale sample, a failed
 * last run) appear only when they apply. Rows that are always meaningful (the
 * status, the sample count, the three figures) always appear, as a dash when
 * the server withheld them.
 */
export function CostInputs({ cost }: { cost: ScheduleLoadCost }) {
  const items: { label: ReactNode; value: ReactNode }[] = [
    {
      label: 'Cost',
      value: cost.status === 'Unknown'
        ? `Unknown — ${cost.unknownReason ?? 'the server gave no reason'}`
        : 'Estimated from its own history',
    },
    {
      label: 'Samples used',
      value: `${exactNumber(cost.sampleCount)} — the most recent qualifying runs, up to ${exactNumber(cost.sampleWindow)}; `
        + `at least ${exactNumber(cost.minimumSamples)} are needed for an estimate`,
    },
    { label: 'Median (p50)', value: durationLabel(cost.p50Seconds) },
    {
      // Nearest-rank p90 over fewer than ten samples IS the maximum, and the
      // label says so rather than letting two identical figures look like a
      // coincidence.
      label: cost.sampleCount < 10 ? 'p90 (below 10 samples, the maximum)' : 'p90',
      value: durationLabel(cost.p90Seconds),
    },
    { label: 'Slowest observed', value: durationLabel(cost.maxSeconds) },
    {
      label: 'Holds a slot for',
      value: `typical ${durationLabel(cost.typicalSeconds)} · planning ${durationLabel(cost.planningSeconds)}`
        + ` · worst ${durationLabel(cost.worstSeconds)}`,
    },
  ]

  if (cost.planningBasis) items.push({ label: 'Planning basis', value: cost.planningBasis })

  if (cost.oldestSampleAt != null || cost.newestSampleAt != null) {
    items.push({
      label: 'Samples span',
      value: `${whenLabel(cost.oldestSampleAt)} to ${whenLabel(cost.newestSampleAt)}`,
    })
  }

  if (cost.historySharedAcrossSchedules) {
    items.push({
      label: 'Run log shared',
      value: `By ${exactNumber(cost.sharingScheduleCount)} active schedules of the same report, so it is planned `
        + 'at its maximum — the log does not say which schedule each run belonged to',
    })
  }

  if (cost.samplesDiscardedAsStale > 0) {
    items.push({
      label: 'Discarded as stale',
      value: `${exactNumber(cost.samplesDiscardedAsStale)} run(s) from before the report definition last changed `
        + `(${whenLabel(cost.definitionChangedAt)})`,
    })
  }

  if (cost.editedSinceNewestSample) {
    items.push({
      label: 'Edited since its newest sample',
      value: 'Yes — the schedule changed after the runs it is costed from, so its parameters may differ',
    })
  }

  if (cost.historyExcludesFailedAttempts) {
    items.push({
      label: 'A floor, not an estimate',
      value: 'The last run failed in a way Hangfire retries, and failed attempts write no history — the real '
        + 'cost is higher by an amount nobody recorded',
    })
  }

  if (cost.endToEndLastRunSeconds != null) {
    items.push({ label: 'Last delivery, end to end', value: durationLabel(cost.endToEndLastRunSeconds) })
  }

  if (cost.deliveryOverheadSeconds != null) {
    items.push({
      label: 'Delivery overhead beyond the query',
      value: durationLabel(cost.deliveryOverheadSeconds),
    })
  }

  if (cost.pooledP90Seconds != null) {
    items.push({
      label: 'The report’s pooled p90',
      value: `${durationLabel(cost.pooledP90Seconds)} over ${exactNumber(cost.pooledSampleCount ?? 0)} run(s) — `
        + 'context only, never used as this schedule’s cost',
    })
  }

  if (cost.uncertaintyHorizonSeconds != null || cost.uncertaintyHorizonBasis) {
    items.push({
      label: 'Keeps later quarter-hours doubtful for',
      value: cost.uncertaintyHorizonSeconds == null
        ? `${NOT_MEASURED}${cost.uncertaintyHorizonBasis ? ` — ${cost.uncertaintyHorizonBasis}` : ''}`
        : `${durationLabel(cost.uncertaintyHorizonSeconds)}`
          + `${cost.uncertaintyHorizonBasis ? ` — ${cost.uncertaintyHorizonBasis}` : ''}`,
    })
  }

  return (
    <Stack gap="xs">
      <MetaList items={items} />
      {cost.caveats.length > 0 && (
        <div>
          <Text size="xs" fw={600} mb={4}>Caveats the server attached</Text>
          <List size="xs" spacing={2}>
            {cost.caveats.map((caveat) => <List.Item key={caveat}>{caveat}</List.Item>)}
          </List>
        </div>
      )}
    </Stack>
  )
}
